import { useRef, useCallback } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  useToast,
} from "@chakra-ui/react";
import { Form, Formik, useFormikContext } from "formik";
import * as yup from "yup";
import { FiCalendar, FiPaperclip, FiUsers, FiX } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { DateTimePickerWithLabel } from "newui/components/ui/InputWithLabel/DateTimePickerWithLabel";
import { TextInputWithLabel } from "newui/components/ui/InputWithLabel/TextInputWithLabel";
import { TextAreaWithLabel } from "newui/components/ui/InputWithLabel/TextAreaWithLabel";
import dayjs from "newui/utils/dayjs";
import type { Meeting, CreateMeetingRequest } from "../../types/meeting.type";
import EmailPreview from "pages/EmailPreview/EmailPreview";
import { PanelMeetingInvitationEmailContent } from "components/email-template/PanelMeetingInvitationEmailContent";

interface MeetingInviteDrawerProps {
  attendeeCount: number;
  existingMeeting: Meeting | undefined;
  isOpen: boolean;
  rfxLeadName: string;
  rfxTitle: string;
  onClose: () => void;
  onSave: (payload: CreateMeetingRequest) => Promise<void>;
  onSend: (payload: CreateMeetingRequest) => Promise<void>;
}

interface MeetingFormValues {
  location: string;
  meetingDate: Date;
  meetingTime: string;
  meetingTimezone: string;
  message: string;
}

const validationSchema = yup.object().shape({
  meetingDate: yup.date().required("Meeting date is required"),
  meetingTime: yup.string().required("Meeting time is required"),
  meetingTimezone: yup.string().required("Timezone is required"),
  location: yup.string().max(500, "Location must be at most 500 characters"),
  message: yup.string().max(2000, "Message must be at most 2000 characters"),
});

const buildScheduledAtISO = (date: Date, time: string, timezone: string): string => {
  const dateStr = dayjs(date).format("YYYY-MM-DD");
  return dayjs.tz(`${dateStr} ${time}`, "YYYY-MM-DD hh:mm A", timezone).toISOString();
};

const getTimezoneAbbr = (date: Date, tz: string): string => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(date);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? tz;
  } catch {
    return tz;
  }
};

const formatDateTimeDisplay = (date: Date, time: string, timezone: string): string => {
  const dateStr = dayjs(date).format("YYYY-MM-DD");
  const formatted = dayjs
    .tz(`${dateStr} ${time}`, "YYYY-MM-DD hh:mm A", timezone)
    .format("MMM D, YYYY [at] h:mm A");
  const abbr = getTimezoneAbbr(date, timezone);
  return `${formatted} ${abbr}`;
};

/** Syncs Formik dirty/values into a parent ref so the close handler can access them */
const FormStateSync: React.FC<{
  stateRef: React.MutableRefObject<{ isValid: boolean; dirty: boolean; values: MeetingFormValues; }>;
}> = ({ stateRef }) => {
  const { dirty, values, isValid } = useFormikContext<MeetingFormValues>();
  stateRef.current = { dirty, values, isValid };
  return null;
};

export const MeetingInviteDrawer: React.FC<MeetingInviteDrawerProps> = ({
  isOpen,
  onClose,
  rfxLeadName,
  rfxTitle,
  attendeeCount,
  existingMeeting,
  onSave,
  onSend,
}) => {
  const toast = useToast();
  const formStateRef = useRef<{ isValid: boolean; dirty: boolean; values: MeetingFormValues; }>({
    dirty: false,
    values: {} as MeetingFormValues,
    isValid: false,
  });

  const handleClose = useCallback(async () => {
    const { dirty, values, isValid } = formStateRef.current;
    if (dirty && isValid) {
      try {
        await onSave(buildPayload(values));
      } catch {
        // Silent fail — don't block close
      }
    }
    onClose();
  }, [onSave, onClose]);

  const getInitialDate = (): Date => {
    if (existingMeeting?.scheduled_at) {
      return new Date(existingMeeting.scheduled_at);
    }
    return dayjs().add(7, "days").toDate();
  };

  const getInitialTime = (): string => {
    if (existingMeeting?.scheduled_at) {
      return dayjs(existingMeeting.scheduled_at).format("hh:mm A");
    }
    return "10:00 AM";
  };

  const getInitialTimezone = (): string => {
    return existingMeeting?.timezone ?? dayjs.tz.guess();
  };

  const initialValues: MeetingFormValues = {
    meetingDate: getInitialDate(),
    meetingTime: getInitialTime(),
    meetingTimezone: getInitialTimezone(),
    location: existingMeeting?.location ?? "",
    message: existingMeeting?.message ?? "",
  };

  const buildPayload = (values: MeetingFormValues): CreateMeetingRequest => {
    const scheduledAt = buildScheduledAtISO(
      values.meetingDate,
      values.meetingTime,
      values.meetingTimezone
    );
    return {
      scheduled_at: scheduledAt,
      timezone: values.meetingTimezone,
      location: values.location || undefined,
      message: values.message || undefined,
    };
  };

  const handleSaveAndSend = async (
    values: MeetingFormValues,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    try {
      await onSend(buildPayload(values));
      toast({
        title: "Invitations sent",
        description: "Meeting details saved and invitations sent to all attendees.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onClose();
    } catch {
      toast({
        title: "Error",
        description: "Failed to send invitations. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer placement="right" isOpen={isOpen} onClose={handleClose} size="xl">
      <DrawerOverlay onClick={handleClose} />
      <DrawerContent maxWidth="1280px">
        <DrawerHeader className="border-b-2">
          <Flex justifyContent="space-between" align="center">
            <div>
              <p className="text-xl font-semibold text-bg-brand-solid">
                Meeting Details
              </p>
              <p className="text-sm font-normal text-gray-500">
                Set the date, time, and location for the meeting. Details are saved automatically when you close.
              </p>
            </div>
            <button
              onClick={handleClose}
              className="rounded-lg p-2 hover:bg-gray-100"
              aria-label="Close drawer"
              data-testid="close-meeting-drawer"
            >
              <FiX size={20} />
            </button>
          </Flex>
        </DrawerHeader>
        <DrawerBody>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              await handleSaveAndSend(values, setSubmitting);
            }}
            validateOnMount={false}
            enableReinitialize
          >
            {({ isSubmitting, isValid, values, setFieldValue }) => (
              <Form>
                <FormStateSync stateRef={formStateRef} />
                <div className="grid grid-cols-3">
                  <div className="col-span-1 pr-6">
                    <p className="mb-4 text-lg font-semibold text-primary-900">
                      Meeting Details
                    </p>

                    <div className="mb-4">
                      <DateTimePickerWithLabel
                        label="Meeting Date & Time"
                        dateFieldName="meetingDate"
                        timeFieldName="meetingTime"
                        timeZoneFieldName="meetingTimezone"
                        defaultDate={values.meetingDate}
                        defaultDaysFromToday={7}
                        defaultTime={values.meetingTime}
                        required
                        onValueChanged={(date, time, tz) => {
                          setFieldValue("meetingDate", date);
                          setFieldValue("meetingTime", time);
                          setFieldValue("meetingTimezone", tz);
                        }}
                      />
                    </div>

                    <div className="mb-4">
                      <TextInputWithLabel
                        label="Location (optional)"
                        placeholder="e.g. Conference Room A or https://meet.example.com/..."
                        name="location"
                        value={values.location}
                        onChange={(e) => setFieldValue("location", e.target.value)}
                      />
                    </div>

                    <div className="mb-6">
                      <TextAreaWithLabel
                        label="Message (optional)"
                        placeholder="Add context or agenda for attendees..."
                        name="message"
                        value={values.message}
                        onChange={(e) => setFieldValue("message", e.target.value)}
                        maxLength={2000}
                      />

                    </div>

                    <Flex gap={3} justify="flex-end" wrap="wrap">
                      <Button
                        btntype="secondary"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        data-testid="cancel-meeting-btn"
                      >
                        Close
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting || !isValid || attendeeCount === 0}
                        isLoading={isSubmitting}
                        data-testid="send-invites-btn"
                      >
                        <FiCalendar className="h-4 w-4 mr-2" />
                        Send Invitations
                      </Button>
                    </Flex>
                  </div>

                  <div className="col-span-2 pl-6 border-l border-gray-100">
                    <p className="text-lg font-semibold text-primary-900 mb-4">
                      Calendar Invite Summary
                    </p>
                    <div className="space-y-4 text-sm text-gray-700 mb-4">
                      <div className="flex items-start gap-3">
                        <FiUsers className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-gray-900">Attendees</p>
                          <p className="text-gray-500">
                            {attendeeCount} attendee{attendeeCount !== 1 ? "s" : ""} will be invited
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <FiPaperclip className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-gray-900">Calendar Attachment</p>
                          <p className="text-gray-500">
                            A .ics calendar file will be attached to the invitation email so attendees
                            can add this meeting to their calendar.
                          </p>
                        </div>
                      </div>
                    </div>
                    <EmailPreview>
                      <PanelMeetingInvitationEmailContent meetingLocation={values.location} meetingDate={values.meetingDate} meetingTime={values.meetingTime} meetingTImeZone={values.meetingTimezone} rfxLeadName={rfxLeadName} rfxTitle={rfxTitle} additionalMessage={values.message} />
                    </EmailPreview>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
